<template>
  <MapHoverPopup v-if="hover" :feature="hoveredFeature ?? null" placement="top">
    <template v-if="hover.type === 'breadcrumb'">
      <div>{{ hover.unit }} ({{ hover.user }}) on {{ hover.timestamp }}</div>
    </template>
    <template v-else>
      <div class="d-flex align-center">
        <v-icon class="mr-1" small>{{ hover.icon }}</v-icon>
        <span>{{ hover.label }}</span>
        <span class="ml-auto">{{ hover.distance }} km</span>
      </div>
      <hr class="my-2" />
      <div class="d-flex align-baseline">
        <span>{{ hover.start }} to {{ hover.end }}</span>
        <span class="ml-auto">{{ hover.duration }} hr</span>
      </div>
      <div class="mt-2">{{ hover.unit }} ({{ hover.user }})</div>
    </template>
  </MapHoverPopup>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { HoveredMapFeature } from '../mapbox/MapHoverState'
import MapHoverPopup from '../mapbox/MapHoverPopup.vue'
import { format, isSameDay, parseISO } from 'date-fns'

const props = defineProps<{
  hoveredFeature?: HoveredMapFeature
}>()

type HoverContent =
  | { type: 'breadcrumb'; unit: string; user: string; timestamp: string }
  | {
      type: 'segment'
      unit: string
      user: string
      label: string
      icon: string
      distance: string
      start: string
      end: string
      duration: string
    }

const getHoverContent = (
  properties: NonNullable<HoveredMapFeature['properties']>,
): HoverContent | null => {
  const unit = properties.UnitNumber || 'Unknown unit'
  const user = properties.UserName || 'Unknown user'

  if (properties.Type === 'Breadcrumb') {
    return {
      type: 'breadcrumb',
      unit,
      user,
      timestamp: format(parseISO(properties.Timestamp), 'MMM dd, HH:mm'),
    }
  }

  const isRouteSegment =
    properties.Type === 'Route' && ['Travel', 'Stop'].includes(properties.SegmentType)
  const isStopArea = ['StopArea', 'StopAreaMarker'].includes(properties.Type)

  if (!isStopArea && !isRouteSegment) return null

  const startTime = parseISO(properties.StartTime)
  const endTime = parseISO(properties.EndTime)
  const elapsedMilliseconds = endTime.getTime() - startTime.getTime()
  const totalMinutes = Math.round(elapsedMilliseconds / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = String(totalMinutes % 60).padStart(2, '0')

  const start = format(startTime, 'MMM d, HH:mm')
  const end = format(endTime, isSameDay(startTime, endTime) ? 'HH:mm' : 'MMM d, HH:mm')
  const duration = `${hours}:${minutes}`
  const segmentType = isStopArea ? 'Stop' : properties.SegmentType

  return {
    type: 'segment',
    unit,
    user,
    label: segmentType,
    icon: segmentType === 'Stop' ? 'mdi-map-marker' : 'route',
    start,
    end,
    duration,
    distance: (properties.DistanceTraveled / 1000).toFixed(2),
  }
}

const hover = computed(() => {
  const properties = props.hoveredFeature?.properties
  return properties ? getHoverContent(properties) : null
})
</script>
